import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

const AssignedAssetCard = ({ asset, onClick }) => {
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "critical":
      case "high":
        return { backgroundColor: "#FFEBEE", color: "#C62828" };
      case "medium":
        return { backgroundColor: "#FFF3E0", color: "#E65100" };
      case "low":
        return { backgroundColor: "#E8F5E9", color: "#2E7D32" };
      default:
        return { backgroundColor: "#F5F5F5", color: "#757575" };
    }
  };

  const getStatusName = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "online":
        return { backgroundColor: "#E8F5E9", color: "#2E7D32" };
      case "offline":
        return { backgroundColor: "#EEEEEE", color: "#616161" };
      default:
        return { backgroundColor: "#F5F5F5", color: "#757575" };
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return "#22C55E";
    if (score >= 75) return "#f9a320";
    return "#DC2626";                 
  };

  const formatSensorValue = (value) => {
  const match = String(value).match(/^-?[\d.]+/);
  if (!match) return "NA";
    
  const num = parseFloat(match[0]);
  if (num <= 0) return "NA";
    
  return value;
};

const isSensorAbnormal = (value, normal) => {
  const match = String(value).match(/^-?[\d.]+/);
  if (!match) return false;

  const numValue = parseFloat(match[0]);
  if (numValue <= 0) return false;

  const [min, max] = normal.split("-").map(parseFloat);
  return numValue < min || numValue > max;
};

  const getBorderColor = (alertType) => {
    switch (alertType) {
      case "error":
        return "#F44336";
      case "warning":
        return "#FF9800";
      default:
        return "#E0E0E0";
    }
  };

  const sensorEntries = Object.entries(asset.sensors);

  const SensorBox = ({ sensorKey, sensor }) => {
    const isAbnormal = isSensorAbnormal(sensor.value, sensor.normal);
    return (
      <Box
        key={sensorKey}
        sx={{
          textAlign: "center",
          p: 1.5,
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: 80,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#757575",
            fontSize: "0.7rem",
            display: "block",
            mb: 0.5,
            lineHeight: 1.2,
          }}
        >
          {sensor.label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: isAbnormal ? "#F57C00" : "#212121",
            mb: 0.5,
          }}
        >
          {formatSensorValue(sensor.value)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#9E9E9E", fontSize: "0.65rem" }}
        >
          Normal: {sensor.normal}
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "1px solid #E0E0E0",
        boxShadow: "none",
        borderRadius: "12px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
          border: `2px solid ${getBorderColor(asset.alert.type)}`,
        },
      }}
    >
      <CardContent
        sx={{ p: 3, display: "flex", flexDirection: "column", flex: 1 }}
      >
        {/* Top section — flex:1 pushes everything below to consistent position */}
        <Box sx={{ flex: 1 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
              {/* Line 1: name + priority chip */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: "1.125rem" }}
                >
                  {asset.name}
                </Typography>
                <Chip
                  label={asset.priority}
                  size="small"
                  sx={{
                    ...getPriorityColor(asset.priority),
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: "22px",
                  }}
                />
              </Box>

              {/* Line 2: tag */}
              {asset.tag && (
                <Typography
                  variant="body2"
                  sx={{ color: "#717070", fontSize: "0.875rem", mb: 0.5 }}
                >
                  {asset.tag}
                </Typography>
              )}

              {/* Line 3: plant › area */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#757575", fontSize: "0.875rem" }}
                >
                  {asset.location.workspace} › {asset.location.zone}
                </Typography>
              </Box>
            </Box>

            <Chip
              label={getStatusName(asset.status)}
              size="small"
              sx={{
                ...getStatusColor(asset.status),
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "22px",
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>

        {/* Failure Probability Alert — always at same vertical position */}
        <Alert
          severity={asset.alert.type === "error" ? "error" : "warning"}
          icon={asset.alert.type === "error" ? <ErrorIcon /> : <WarningIcon />}
          sx={{
            mb: 2,
            alignItems: "center",
            borderRadius: "8px",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {asset.alert.message}
            </Typography>
            <Chip
              label={asset.alert.confidence}
              size="small"
              sx={{
                backgroundColor:
                  asset.alert.type === "error" ? "#FFCDD2" : "#FFE0B2",
                color: asset.alert.type === "error" ? "#C62828" : "#E65100",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "20px",
                flexShrink: 0,
                ml: 1,
              }}
            />
          </Box>
        </Alert>

        {/* Health Score — always at same vertical position */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#424242" }}
            >
              Asset Health Score
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.5rem" }}
            >
              {asset.healthScore}/100
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={asset.healthScore}
            sx={{
              height: 8,
              borderRadius: 1,
              backgroundColor: "#E0E0E0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: getHealthScoreColor(asset.healthScore),
                borderRadius: 1,
              },
            }}
          />
        </Box>

        {/* RUL / Work Orders / Next Service */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            mb: 2,
          }}
        >
          {[
            {
              label: asset.rul.label,
              value: asset.rul.value,
              color: asset.alert.type === "error" ? "#F44336" : "#E65100",
            },
            { label: asset.workOrders.label, value: asset.workOrders.value },
            {
              label: asset.nextService.label,
              value: asset.nextService.value,
              fontSize: "0.875rem",
            },
          ].map((item, i) => (
            <Box key={i}>
              <Typography
                variant="caption"
                sx={{ color: "#757575", fontSize: "0.75rem" }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: item.color || "inherit",
                  fontSize: item.fontSize || "1rem",
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Sensors — row of 3 + row of 2 centered */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.5,
            }}
          >
            {sensorEntries.slice(0, 3).map(([key, sensor]) => (
              <SensorBox key={key} sensorKey={key} sensor={sensor} />
            ))}
          </Box>
          {sensorEntries.length > 3 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1.5,
                px: { xs: 0, sm: "12.5%" },
              }}
            >
              {sensorEntries.slice(3).map(([key, sensor]) => (
                <SensorBox key={key} sensorKey={key} sensor={sensor} />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssignedAssetCard;
